<script setup lang="ts">
import { formatDatetime } from "@/utils/toFormattedDatetime";
import { Map } from "@iconoir/vue";
import type { PublicCanvass } from "./types";
import { useReachedCanvassTargets } from "./useReachedCanvassTargets";

const { canvass } = defineProps<{
  canvass: Pick<
    PublicCanvass,
    "name" | "meetingPoint" | "abstract" | "canvassTargetsWithProgress"
  >;
}>();

const { reachedCanvassTargetsCount } = useReachedCanvassTargets(
  () => canvass.canvassTargetsWithProgress,
);
</script>

<template>
  <h1 class="text-default">
    Haustüraktion <br />
    <span class="text-large">{{ canvass.name }}</span>
  </h1>

  <section v-if="canvass.meetingPoint.time !== null">
    <h2 class="text-default">Aktionsstart</h2>
    <p class="text-large">
      {{ formatDatetime(canvass.meetingPoint.time) }}
    </p>
  </section>

  <section v-if="canvass.meetingPoint.description !== null">
    <h2 class="text-default">Treffpunkt</h2>
    <p class="text-large">{{ canvass.meetingPoint.description }}</p>
    <div v-if="canvass.meetingPoint.coordinates !== null" class="row">
      <a
        :href="`https://www.openstreetmap.org/search?lat=${canvass.meetingPoint.coordinates[1]}&lon=${canvass.meetingPoint.coordinates[0]}&zoom=16`"
        target="_blank"
        ><Map />OpenStreetMap</a
      >
      <a
        :href="`https://www.google.de/maps/search/${canvass.meetingPoint.coordinates[1]},${canvass.meetingPoint.coordinates[0]}`"
        target="_blank"
        ><Map />Google Maps</a
      >
    </div>
  </section>

  <section v-if="canvass.abstract !== null" class="column">
    <h2 class="text-default">Kurzbeschreibung</h2>
    <p class="text-box">{{ canvass.abstract }}</p>
  </section>

  <section v-if="canvass.canvassTargetsWithProgress.length > 0" class="column">
    <div>
      <h2 class="text-default">Gesprächsziele</h2>
      <p class="muted">
        {{ reachedCanvassTargetsCount }} von
        {{ canvass.canvassTargetsWithProgress.length }} Zielen erreicht!
      </p>
    </div>
    <dl class="column">
      <div
        v-for="canvassTarget in canvass.canvassTargetsWithProgress"
        :key="canvassTarget.id"
        class="canvass-target row row-md"
      >
        <dt class="text-box">{{ canvassTarget.description }}</dt>
        <dd class="text-semibold">
          {{ canvassTarget.satisfiedTargets }}/{{ canvassTarget.target }}
        </dd>
      </div>
    </dl>
  </section>
</template>

<style scoped>
.text-box {
  background-color: var(--bg-color-input);
  padding: var(--padding-input);
  border-radius: var(--border-radius-input);
}

.canvass-target {
  dt {
    flex-grow: 1;
  }
}
</style>
